'use client';

import { Suspense, startTransition, useState } from 'react';

import dynamic from 'next/dynamic';

import { currencyFormatter } from '@ps/functions/utils/currency-formatter';
import clsx from 'clsx';
import { format } from 'date-fns';

import type { ChatOrderMessage } from '../../types/ChatMessage';
import { MessageBubbleShell } from './MessageBubbleShell';

// Interaction-only: the order-detail view drags in the OrderApi + all the
// detail cards, none of which the chat list needs until a card is tapped.
const importOrderDetailModal = () =>
  import('./order-detail/ChatOrderDetailModal');

const ChatOrderDetailModal = dynamic(() =>
  importOrderDetailModal().then(mod => mod.ChatOrderDetailModal),
);

interface Props {
  message: ChatOrderMessage;
  isOwn: boolean;
  currentUserId: string;
}

/**
 * Order bubble — inline card built from `message.content` (no API
 * fetch). Mirrors the PopshopRN layout (image + order id/date + title
 * + price + owner) but uses the room's mint/neutral palette instead
 * of the legacy purple gradient. Tapping the card opens the shared
 * order-detail view as a modal, in the buyer or seller variant
 * depending on which side of the order the viewer is on.
 */
export function OrderMessage({ message, isOwn, currentUserId }: Props) {
  const order = message.content;
  const ownerName =
    order.from === 'seller' ? order.sellerName : order.buyerName;
  const orderDate = order.orderDate ? new Date(order.orderDate) : undefined;
  const [detailOpen, setDetailOpen] = useState(false);

  const openDetail = () => {
    // Warm the chunk, then flip state inside a transition so the chat stays
    // painted while it resolves. Paired with the local <Suspense> below, this
    // keeps the first-open suspension from bubbling to the route boundary
    // (which remounted the whole chat). See lazy-modal Suspense-bubbling.
    void importOrderDetailModal();
    startTransition(() => setDetailOpen(true));
  };

  // The VIEWER's relationship to the order picks the detail variant — the
  // seller sees the Store Orders view, the buyer the My Purchases view.
  // Fall back to the sharer's perspective if the ids don't identify us.
  const mode =
    currentUserId === order.sellerId
      ? 'seller'
      : currentUserId === order.buyerId
        ? 'buyer'
        : order.from;

  return (
    <MessageBubbleShell message={message} isOwn={isOwn} bare>
      <button
        type="button"
        onClick={openDetail}
        onPointerEnter={() => void importOrderDetailModal()}
        aria-label={`View order #${order.orderNumber ?? ''}`}
        className={clsx(
          // Width follows the PopshopRN `useMessageMaxWidth` rule:
          // `min(315px, 55% of message-list width)`. The message list
          // sets `container-type: inline-size`.
          'flex w-[min(315px,55cqw)] flex-col gap-2 overflow-hidden rounded-2xl p-1.5 text-left transition active:scale-[0.99]',
          isOwn ? 'bg-brand' : 'bg-surface-3',
          isOwn ? 'rounded-br-sm' : 'rounded-bl-sm',
        )}
      >
        {order.imageUrl ? (
          // Plain <img> — same rationale as ImageMessage (remote
          // firebase storage urls aren't in the Next image config).
          <img
            src={order.imageUrl}
            alt={order.orderTitle ?? ''}
            className="bg-surface-1 aspect-[6/5] w-full rounded-lg object-cover"
            loading="lazy"
          />
        ) : null}
        <div
          className={clsx(
            'flex flex-col gap-0.5 px-1.5 pb-1.5 pt-0.5',
            isOwn ? 'text-black' : 'text-content-1',
          )}
        >
          <div
            className={clsx(
              'flex items-center gap-2 text-[11px] uppercase',
              isOwn ? 'text-sink/60' : 'text-content-3',
            )}
          >
            <span className="truncate font-semibold">
              Order #{order.orderNumber ?? order.orderId?.slice(0, 8) ?? 'n/a'}
            </span>
            {orderDate ? (
              <span className="tabular-nums">
                {format(orderDate, 'E d').toUpperCase()}
              </span>
            ) : null}
          </div>
          <p className="truncate text-sm font-semibold">
            {order.orderTitle ?? ''}
          </p>
          <p className="text-sm font-semibold">
            {currencyFormatter.formatUSD(order.price)}
          </p>
          {ownerName ? (
            <p
              className={clsx(
                'text-xs',
                isOwn ? 'text-sink/60' : 'text-content-2',
              )}
            >
              By {ownerName}
            </p>
          ) : null}
        </div>
      </button>
      {detailOpen ? (
        // Local boundary: contain the lazy chunk's first-open suspension here
        // so it never reaches the route-level fallback.
        <Suspense fallback={null}>
          <ChatOrderDetailModal
            orderId={order.orderId}
            mode={mode}
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
          />
        </Suspense>
      ) : null}
    </MessageBubbleShell>
  );
}
